'use client'

import dynamic from 'next/dynamic'
import { cn } from 'lib/utils'
import { ComponentType } from 'react'
import { WidgetData } from 'app/types/widget-data'

// Dynamically import widgets
// Note: All widgets are client components because they use hooks (useState, useEffect)
// and need interactivity (carousels, forms, etc.)
const widgets: Record<WidgetType, ComponentType<any>> = {
    NAVBAR: dynamic(() => import('./Widgets/NavbarWidget')),
    HERO: dynamic(() => import('./Widgets/HeroWidget')),
    MENU: dynamic(() => import('./Widgets/MenuWidget')),
    STATS: dynamic(() => import('./Widgets/StatsWidget')),
    COMP: dynamic(() => import('./Widgets/CompetitionCardWidget')),
    GALLERY: dynamic(() => import('./Widgets/GalleryWidget')),
    CAROUSEL: dynamic(() => import('./Widgets/CarouselWidget')),
    CARD: dynamic(() => import('./Widgets/CardWidget')),
    SOCIAL: dynamic(() => import('./Widgets/SocialWidget')),
    FEED: dynamic(() => import('./Widgets/FeedWidget')),
    STREAM: dynamic(() => import('./Widgets/StreamWidget')),
    TESTIMONIALS: dynamic(() => import('./Widgets/TestimonialsWidget')),
    STEP: dynamic(() => import('./Widgets/StepWidget')),
    FEATURES: dynamic(() => import('./Widgets/FeaturesWidget')),
    PRICING: dynamic(() => import('./Widgets/PricingWidget')),
    CONTACT: dynamic(() => import('./Widgets/ContactWidget')),
    FAQ: dynamic(() => import('./Widgets/FAQWidget')),
    FORM: dynamic(() => import('./Widgets/FormWidget')),
    TEXT: dynamic(() => import('./Widgets/TextWidget')),
    RICHTEXT: dynamic(() => import('./Widgets/RichTextWidget')),
    TOTW: dynamic(() => import('./Widgets/TOTWWidget')),
    POTW: dynamic(() => import('./Widgets/POTWWidget')),
    TEAM: dynamic(() => import('./Widgets/TeamWidget')),
    FULLCONTENT: dynamic(() => import('./Widgets/FullContentWidget')),
    DYNAMICCONTENT: dynamic(() => import('./Widgets/DynamicContentWidget')),
    STANDINGSTAT: dynamic(() => import('./Widgets/StandingStatWidget')),
    FOOTER: dynamic(() => import('./Widgets/FooterWidget')),
    NEWS: dynamic(() => import('./Widgets/NewsWidget')),
    PAGENEWS: dynamic(() => import('./Widgets/PageNewsWidget'))
}

interface PageLayoutProps {
    lang: string
    layout: IPage
    className?: string
    config?: IDomainConfig
    /**
     * Pre-fetched widget data from server component.
     * This allows widgets to skip client-side API calls when data is already available.
     * All data must be JSON-serializable (no functions, Dates, etc.)
     * 
     * Pages should construct this object based on which widgets they use.
     * Each widget type has its own data structure defined in app/types/widget-data.d.ts
     * 
     * Example:
     * ```typescript
     * const widgetData: WidgetData = {
     *   CAROUSEL: { items: [...] },
     *   STANDINGSTAT: { topTeams: [...], topPlayers: {...} }
     * }
     * ```
     */
    widgetData?: WidgetData | null
}

export default function PageLayout({ lang, layout, className, config, widgetData }: PageLayoutProps) {
    const { widgets: layoutWidgets, styles } = layout

    return (
        <div
            className={cn(
                layoutWidgets.length > 0 ? 'min-h-screen' : '',
                {
                    'container mx-auto': styles?.layout === 'fixed',
                    'w-full': styles?.layout === 'fluid'
                },
                {
                    'space-y-4': styles?.spacing === 'compact',
                    'space-y-8': styles?.spacing === 'comfortable',
                    'space-y-12': styles?.spacing === 'spacious'
                },
                className
            )}
        >
            {layoutWidgets.length > 0 &&
                layoutWidgets
                    .filter(widget => widget.isVisible)
                    .sort((a, b) => a.order - b.order)
                    .map((widget, index) => {
                        const Widget = widgets[widget.type]
                        if (!Widget) return null

                        if (widget.type === 'NAVBAR') {
                            const styleType = (widget.data as { styleType?: WidgetStyleType })?.styleType || 'default'
                            if (styleType !== 'polygon_style') {
                                return null
                            }
                        }

                        return (
                            <div
                                key={`${widget.type}-${index}`}
                                className={cn('w-full', {
                                    'animate-fade-in': widget?.animations?.type === 'fade',
                                    'animate-slide-in': widget?.animations?.type === 'slide',
                                    'animate-zoom-in': widget?.animations?.type === 'zoom'
                                })}
                                style={{
                                    animationDuration: `${widget?.animations?.duration || 0.3}s`
                                }}
                            >
                                <Widget
                                    lang={lang}
                                    data={widget?.data}
                                    styles={widget?.styles}
                                    responsive={widget?.responsive}
                                    config={config}
                                    styleType={(widget.data as { styleType?: WidgetStyleType })?.styleType}
                                    widgetData={widgetData?.[widget.type as keyof typeof widgetData] || null}
                                />
                            </div>
                        )
                    })}
        </div>
    )
}
